import * as THREE from 'three';
import { Node, NodeMaterial } from 'three/webgpu';
import {
  Fn, uniform, vec2, vec3, vec4, float,
  sin, cos, mix, smoothstep, pow, max, normalize, dot, reflect,
  positionLocal, positionPrevious, positionWorld, cameraPosition
} from 'three/tsl';
import { fbm2 } from './tsl/noise';

// Sea surface for the "Sinking Ship" map.
//
// NOTE: NOT currently in use — the sinking-ship map ships with three's
// `WaterMesh` addon (see bundles/sinking-ship/behaviors/oceanWater.ts).
// This procedural fallback is kept around so a single edit to the
// material preset registry + the map.json behaviors entry can swap
// back to it (e.g. as a perf escape hatch on devices where the
// reflector render-target cost shows up). Don't delete unless you've
// committed to the addon path.
//
// Composition (single-pass fragment + animated vertex displacement):
//
//   * Vertex displacement     — sum of 5 Gerstner waves at world XZ. Both
//                               horizontal and vertical components, so
//                               crests bunch up like real swell rather
//                               than smearing into pure sinusoids.
//   * Analytic surface normal — derivatives of the same Gerstner sum, so
//                               shading agrees with geometry vertex-for-
//                               vertex (no normal map needed).
//   * Capillary ripples       — fbm-driven micro-perturbation of the
//                               normal so the surface never reads as
//                               glassy at distance.
//   * Fresnel reflection      — Schlick approximation between deep-water
//                               base + bright sky-tint reflection. View
//                               angle drives the mix so the sea fades
//                               from teal under foot to silver-blue at
//                               the horizon.
//   * Sun glint               — Blinn-Phong-style highlight from the same
//                               sunDir the rest of the scene uses.
//   * Crest foam              — soft white where (height + slope) crosses
//                               a threshold. Gives wave tops a sea-froth
//                               edge without needing a foam mask texture.
//
// Sampling is anchored to world XZ (`positionWorld.xz` + `positionLocal.xy`
// before rotation) so the wave field doesn't crawl when the camera moves.
//
// `lights = false` because water doesn't sit in the standard PBR pipeline
// here — the fresnel + sun glint we compute IS the lighting model. The
// renderer's IBL still influences nearby boat hull shading; this material
// just doesn't read from it.
export class WaterMaterial extends NodeMaterial {
  readonly uTime = uniform(0);

  // Deep water + shallow water tints. Mixed by the wave depth proxy so
  // troughs read deeper than crests. The shallow tint is what's revealed
  // when a wave thins out — slightly greener and brighter.
  readonly uDeepColor    = uniform(new THREE.Vector3(0.012, 0.060, 0.110));
  readonly uShallowColor = uniform(new THREE.Vector3(0.055, 0.235, 0.305));

  // Sky-reflection tint. Mixed in by the Schlick fresnel term. Tuned to
  // match the SeaSky's mid-altitude band so the water reads as continuous
  // with the sky at the horizon.
  readonly uSkyColor     = uniform(new THREE.Vector3(0.62, 0.78, 0.92));

  // Foam colour + how aggressively crests pick it up. uFoamGain raises
  // every contribution; uFoamSharpness is the smoothstep edge tightness.
  readonly uFoamColor     = uniform(new THREE.Vector3(0.95, 0.97, 1.00));
  readonly uFoamGain      = uniform(0.85);
  readonly uFoamSharpness = uniform(0.42);

  // Direction toward the sun, world space. Defaults match the seaSky's
  // sunDir constant so a single source of truth aligns sky + glint.
  readonly uSunDir        = uniform(new THREE.Vector3(-0.42, 0.78, 0.46).normalize());
  // Sun colour for the specular glint. Slightly warmer than pure white so
  // the highlight sits naturally against the cool deep tint.
  readonly uSunColor      = uniform(new THREE.Vector3(1.00, 0.95, 0.85));

  // Vertex-displacement scale. 1 = author-tuned; drop to 0 for a flat
  // testing surface, push to 1.5 for storm-scale swells.
  readonly uWaveAmp       = uniform(1.0);
  // Time multiplier for every wave + ripple. 1 = normal speed.
  readonly uWaveSpeed     = uniform(1.0);
  // Capillary ripple amplitude — purely a normal-map term, no geometry
  // contribution. Adds high-frequency sparkle.
  readonly uRippleAmp     = uniform(0.10);

  // Fresnel R0 (Schlick reflectance at normal incidence). Real water sits
  // around 0.02; nudging it up a touch gives the deep parts a richer
  // reflective body without flattening the contrast against the horizon.
  readonly uFresnelR0     = uniform(0.025);

  constructor() {
    super();
    // Self-shaded — see the class docstring. Still receives shadows from
    // the directional light pass implicitly via the renderer's depth, but
    // doesn't apply diffuse Lambert.
    this.lights = false;

    // ── Gerstner sum. Returns vec4(dispX, dispY (height), dispZ, packedSlope).
    //    The packedSlope is encoded as float because TSL Fn() return shape
    //    is fixed; we instead emit a separate normal-builder below.
    //
    // Five waves with hand-picked directions / wavelengths / steepnesses.
    // Pre-normalised directions so the wavenumber `k = 2π/λ` reads in
    // world metres. Speeds come from deep-water dispersion (c = sqrt(g/k))
    // tuned slightly for visual cadence.
    //
    // Steepness `Q` is per-wave: Σ(Qᵢ × Aᵢ × kᵢ) ≤ 1 keeps crests from
    // overlapping into loops. We use Q ≈ 0.55 / (kᵢAᵢ × N) per wave so
    // the sum stays under 1 even at peak amp.
    const waveDefs = [
      { dx:  0.985, dz:  0.174, lambda: 22.0, A: 0.45, speed: 0.25, Q: 0.40 },
      { dx: -0.643, dz:  0.766, lambda: 13.0, A: 0.30, speed: 0.35, Q: 0.45 },
      { dx:  0.500, dz: -0.866, lambda:  7.5, A: 0.18, speed: 0.45, Q: 0.55 },
      { dx: -0.342, dz: -0.940, lambda:  4.2, A: 0.10, speed: 0.75, Q: 0.55 },
      { dx:  0.866, dz:  0.500, lambda:  2.6, A: 0.05, speed: 1.0, Q: 0.55 }
    ];

    // World-XZ sampling helper. The plane geometry is rotated -90° around X
    // (positionLocal.y maps to world -Z), so we recover world XZ from
    // positionLocal.xy explicitly. The fragment path uses positionWorld.xz
    // directly — both must agree for the wave geometry / shading match.
    const localToWorldXZ = Fn((args: [Node<'vec3'>]) => {
      const [pl] = args;
      return vec2(pl.x, pl.y.negate());
    });

    // Gerstner displacement. Returns:
    //   .xyz = (offsetX, height, offsetZ) — full 3D displacement of the
    //          vertex/sample point relative to its rest position.
    //   .w   = "crest factor" in [-1, 1] used for foam shading; sums each
    //          wave's sin term scaled by amplitude, so it tracks "is this
    //          part of the surface near a crest?" without re-evaluating
    //          the trig.
    const gerstner = Fn((args: [Node<'vec2'>, Node<'float'>]) => {
      const [worldXZ, t] = args;
      const dx = float(0).toVar();
      const dy = float(0).toVar();
      const dz = float(0).toVar();
      const crest = float(0).toVar();
      for (const w of waveDefs) {
        const k = (2 * Math.PI) / w.lambda;
        const omega = k * w.speed;
        const D = vec2(w.dx, w.dz);
        const phase = worldXZ.dot(D).mul(k).sub(t.mul(omega));
        const s = sin(phase);
        const c = cos(phase);
        const A = float(w.A).mul(this.uWaveAmp);
        // Standard Gerstner: vertical = A·sin, horizontal = -Q·A·D·cos.
        dy.addAssign(s.mul(A));
        dx.subAssign(c.mul(A.mul(w.Q * w.dx)));
        dz.subAssign(c.mul(A.mul(w.Q * w.dz)));
        // Crest factor — positive on tops, negative in troughs; max ≈ ΣA.
        crest.addAssign(s.mul(A));
      }
      return vec4(dx, dy, dz, crest);
    });

    // Analytic normal builder. Sums the partial derivatives of each
    // Gerstner wave's height term with respect to world XZ, then builds a
    // unit normal. Skipping the cross-coupling from the horizontal Gerstner
    // displacement here — at the amplitudes we use, the simplification
    // costs <2° of error and saves a Jacobian inversion per pixel.
    const surfaceNormal = Fn((args: [Node<'vec2'>, Node<'float'>]) => {
      const [worldXZ, t] = args;
      const dHdx = float(0).toVar();
      const dHdz = float(0).toVar();
      for (const w of waveDefs) {
        const k = (2 * Math.PI) / w.lambda;
        const omega = k * w.speed;
        const D = vec2(w.dx, w.dz);
        const phase = worldXZ.dot(D).mul(k).sub(t.mul(omega));
        const c = cos(phase);
        const A = float(w.A).mul(this.uWaveAmp);
        dHdx.addAssign(c.mul(A.mul(k * w.dx)));
        dHdz.addAssign(c.mul(A.mul(k * w.dz)));
      }
      return normalize(vec3(dHdx.negate(), float(1), dHdz.negate()));
    });

    // ── Vertex displacement. The plane is authored facing +Z and rotated
    // x = -π/2 so local +Y maps to world -Z, local +Z to world +Y. Lifting
    // a vertex upward in world means *adding* to its local Z coordinate;
    // sliding it horizontally means deltas to local X (= world X) and
    // local Y (= world -Z).
    //
    // `positionPrevious.assign(displaced)` keeps the velocity MRT honest
    // (same trick LavaMaterial uses) — the static component of our
    // displacement would otherwise show up as a permanent motion-blur
    // streak.
    this.positionNode = Fn(() => {
      const worldXZ = localToWorldXZ(positionLocal);
      const t = this.uTime.mul(this.uWaveSpeed);
      const wave = gerstner(worldXZ, t);
      // Local-space delta:
      //   local.x  ← world.x  (no rotation effect on X)
      //   local.y  ← world -Z (so subtract the world-Z displacement)
      //   local.z  ← world.y  (so add the world height directly)
      const displaced = positionLocal.add(vec3(wave.x, wave.z.negate(), wave.y));
      positionPrevious.assign(displaced);
      return displaced;
    })();

    // ── Fragment shading.
    this.colorNode = Fn(() => {
      const worldP = positionWorld.toVar();
      const worldXZ = worldP.xz;
      const t = this.uTime.mul(this.uWaveSpeed);

      // Geometric normal from the same wave function the vertex path
      // displaced with.
      const baseN = surfaceNormal(worldXZ, t).toVar();

      // Capillary ripple normal perturbation. Two fbm samples at slightly
      // offset coordinates approximate dN/dx and dN/dz cheaply. This is
      // independent of the geometric waves so the surface still looks
      // alive at viewing angles where the gerstner crests are barely
      // visible (looking straight down at a calm patch).
      const rippleScale = 1.4;
      const rippleSpeed = 0.35;
      const rPos  = worldXZ.mul(rippleScale).add(vec2(t.mul(rippleSpeed), 0));
      const rPosA = rPos.add(vec2(0.05, 0));
      const rPosB = rPos.add(vec2(0, 0.05));
      const rh   = fbm2(rPos, 4);
      const rhA  = fbm2(rPosA, 4);
      const rhB  = fbm2(rPosB, 4);
      const rdHdx = rhA.sub(rh).mul(this.uRippleAmp).mul(rippleScale);
      const rdHdz = rhB.sub(rh).mul(this.uRippleAmp).mul(rippleScale);
      const ripplePerturbed = normalize(vec3(
        baseN.x.sub(rdHdx),
        baseN.y,
        baseN.z.sub(rdHdz)
      )).toVar();

      // View vector from the surface point back to the camera.
      const V = normalize(cameraPosition.sub(worldP.xyz)).toVar();

      // Schlick fresnel. Higher at glancing angles → more sky reflection
      // out at the horizon, more deep-water under foot.
      const cosTheta = max(dot(ripplePerturbed, V), float(0)).toVar();
      const fresnel = this.uFresnelR0.add(
        float(1).sub(this.uFresnelR0).mul(pow(float(1).sub(cosTheta), 5))
      ).toVar();

      // Deep ↔ shallow blend keyed on the crest factor we packed into
      // gerstner's .w earlier. Recompute it here cheaply by sampling the
      // height of just the longest (most-influential) wave; saves a full
      // re-sum in the fragment shader. Pure visual aid — no impact on
      // geometry.
      const longK = (2 * Math.PI) / 22.0;
      const longOmega = longK * 0.55;
      const longPhase = worldXZ.dot(vec2(0.985, 0.174)).mul(longK).sub(t.mul(longOmega));
      const heightProxy = sin(longPhase).mul(0.5).add(0.5); // 0..1
      const waterBody = mix(this.uDeepColor, this.uShallowColor, heightProxy);

      // Reflect the view direction across the perturbed normal to read a
      // proxy "sky angle" — we don't actually sample a cubemap (the sea
      // sky shader is enough on its own); instead we modulate the sky
      // tint by how high the reflection points so the upper hemisphere
      // brightens the reflection more than the lower.
      const R = reflect(V.negate(), ripplePerturbed);
      const skyUp = max(R.y, float(0));
      const skyTint = this.uSkyColor.mul(mix(float(0.55), float(1.0), skyUp));

      // Compose deep base + sky reflection by fresnel.
      const reflective = mix(waterBody, skyTint, fresnel);

      // ── Sun glint. Blinn-Phong-ish: half-vector against the light gives
      // a tight specular lobe that sells "sunlit water" without needing
      // an env map. Two-stop power so a soft halo surrounds a hot core.
      const L = normalize(this.uSunDir).toVar();
      const H = normalize(L.add(V));
      const NdotH = max(dot(ripplePerturbed, H), float(0));
      const glintCore = pow(NdotH, float(180));
      const glintHalo = pow(NdotH, float(28)).mul(0.18);
      const glint = this.uSunColor.mul(glintCore.add(glintHalo)).mul(0.9);

      // ── Foam. Thresholded combination of (a) gerstner crest factor
      // (positive at wave tops) and (b) fbm noise so the foam line breaks
      // up rather than running cleanly along iso-height contours. The
      // smoothstep edges are tight so foam reads as a thin bright band on
      // each crest, not a global wash.
      const fullWave = gerstner(worldXZ, t);
      const crest = fullWave.w;
      const foamNoise = fbm2(worldXZ.mul(2.7).add(vec2(t.mul(0.15), 0)), 4);
      const crestNorm = smoothstep(float(0.30), float(0.90), crest.mul(this.uFoamGain));
      const foamMask = smoothstep(this.uFoamSharpness, this.uFoamSharpness.add(0.18), crestNorm.mul(foamNoise.add(0.4)));
      const foam = this.uFoamColor.mul(foamMask);

      // Subtle sub-surface scattering hint — the underside of crests picks
      // up a translucent green tint from light passing through the wave.
      // Modeled as a function of (V · forward-of-light-projection).
      const sssAmount = pow(max(dot(L.negate(), ripplePerturbed), float(0)), 4).mul(crestNorm).mul(0.25);
      const sssColor = vec3(0.10, 0.36, 0.32).mul(sssAmount);

      // Compose. Foam goes last on top, so wave tops still glint where
      // the foam thins out.
      const lit = reflective.add(glint).add(sssColor);
      const final = mix(lit, foam, foamMask.mul(0.85));

      return vec4(final, 1);
    })();
  }

  setTime(t: number): void { this.uTime.value = t; }
}
